import Box from "component/layoutComponents/Box";
import Col from "component/layoutComponents/Col";
import Row from "component/layoutComponents/Row";
import React, { createContext, memo, useContext } from "react";
import {
  TextGray,
  TextBlack,
  StyledSubtitle,
  StyledTitle,
} from "../ProductDetail.styles";
import Divider from "./Divider";
import { useTheme } from "styled-components";
import DefaultUptimeTheme from "component/theme/defaultTheme";
import { Product } from "component/MarketPlace/ProductCard/types";

const NOT_AVAILABLE = "N/A";

const SpecificationsContext = createContext({ hideUnavailable: false });

const SpecificationRow = memo(
  ({ label, value = NOT_AVAILABLE, valueId }: { label: string; value?: string; valueId?: string }) => {
    const context = useContext(SpecificationsContext);
    if (context.hideUnavailable && value === NOT_AVAILABLE) return null;
    const mappedValue = !value || value === 'null' ? NOT_AVAILABLE : value;
    return (
      <>
        <Box display="flex" alignItems="center">
          <Box flexBasis="50%">
            <TextBlack variant="h6" color="text.primary">{label}</TextBlack>
          </Box>
          <Box flexBasis="50%" padding="16px 0" maxWidth="50%">
            <TextGray variant="body1" color="text.secondary" minHeight="24px" id={valueId}>{mappedValue}</TextGray>
          </Box>
        </Box>
      </>
    );
  }
);

const SpecificationsContent = ({ product, hideUnavailable = false, locale }:{ product?:Product, hideUnavailable?: boolean; locale?:any; }) => {
  const theme = useTheme() as unknown as typeof DefaultUptimeTheme;

  return (
    <SpecificationsContext.Provider value={{ hideUnavailable }}>
      <Row>
        <Col colspan={{ xs: 12 }}>
          <StyledTitle variant="h3" color="text.primary">{locale?.productDetail?.specifications}</StyledTitle>
        </Col>
        <Col colspan={{ xs: 12, md: 12, lg: 6 }}>
          <Divider orientation="horizontal" />
          <Box marginBottom={`${theme.uptimeSpace[4]}px`}>
            <StyledSubtitle variant="h5" color="text.primary">{locale?.productDetail?.general}</StyledSubtitle>
          </Box>
          <Box marginLeft={`${theme.uptimeSpace[5]}px`}>
            <SpecificationRow label={locale?.productDetail?.brand} value={product?.brand?.name} />
            <SpecificationRow label={locale?.productDetail?.type} value={product?.category[0]?.name} valueId="lbl-speccontent-type"/>
            <SpecificationRow
              label={locale?.productDetail?.operatingSystem}
              value={product?.specifications?.OperatingSystem?.["specifications.os_type"]}
            />
            <SpecificationRow label={locale?.productDetail?.keyboard} value={product?.specifications?.Keyboard?.["specifications.keyboard"]} />
            <SpecificationRow label={locale?.productDetail?.powerAdapter} value={product?.specifications?.Power?.["specifications.power_adapter"]} />
            <SpecificationRow label={locale?.productDetail?.graphicCard} value={product?.specifications?.Graphic?.["specifications.graphic_processor"]} />
            <SpecificationRow label={locale?.productDetail?.language} value={product?.language} />
            <SpecificationRow
              label={locale?.productDetail?.communications}
              value={product?.specifications?.Communications?.["specifications.communications"]}
            />
            <SpecificationRow label={locale?.productDetail?.warranty} value={NOT_AVAILABLE} />
          </Box>
        </Col>
        <Col colspan={{ xs: 12, md: 12, lg: 6 }}>
          <Divider orientation="horizontal" />
          <Box>
            <Box marginBottom={`${theme.uptimeSpace[4]}px`}>
              <StyledSubtitle variant="h5" color="text.primary">{locale?.productDetail?.display}</StyledSubtitle>
            </Box>
            <Box marginLeft={`${theme.uptimeSpace[5]}px`}>
              <SpecificationRow label={locale?.productDetail?.screenSize} value={product?.specifications?.Display["specifications.display_size"]} />
              <SpecificationRow label={locale?.productDetail?.screenType} value={product?.specifications?.Display["specifications.display_type"]} />
            </Box>
          </Box>
          <Box>
            <Divider orientation="horizontal" padding="20px 0px" />
            <Box marginBottom={`${theme.uptimeSpace[4]}px`}>
              <StyledSubtitle variant="h5" color="text.primary">{locale?.productDetail?.processor}</StyledSubtitle>
            </Box>
            <Box marginLeft={`${theme.uptimeSpace[5]}px`}>
              <SpecificationRow
                label={locale?.productDetail?.processorType}
                value={product?.specifications?.Processor["specifications.processor_type"]}
              />
              <SpecificationRow label={locale?.productDetail?.processorSpeed} value={product?.specifications?.Processor["specifications.processor_speed"]} />
            </Box>
          </Box>
          <Box>
            <Divider orientation="horizontal" padding="20px 0px" />
            <Box marginBottom={`${theme.uptimeSpace[4]}px`}>
              <StyledSubtitle variant="h5" color="text.primary">
                {locale?.productDetail?.memoryStorage}
              </StyledSubtitle>
            </Box>
            <Box marginLeft={`${theme.uptimeSpace[5]}px`}>
              <SpecificationRow label={locale?.productDetail?.RAMCapacity} value={product?.specifications?.["Memory & Storage"]?.["specifications.memory"]} />
              <SpecificationRow label={locale?.productDetail?.storage} value={product?.specifications?.["Memory & Storage"]?.["specifications.storage"]} />
            </Box>
          </Box>
        </Col>
      </Row>
    </SpecificationsContext.Provider>
  );
};

export default memo(SpecificationsContent);
